/**
 * Turns a plate appearance's pitch sequence ("BCFFX") into marks for the
 * scoresheet cell. The server stores the sequence, not a tally, so the reading
 * of it stays here: one mark per pitch, so the number of pitches in the at-bat
 * is legible without counting.
 *
 * The strip runs right to left — the first pitch sits in the corner, where the
 * eye starts, and the at-bat grows inward.
 *
 * Colour alone would not carry it: green and orange are close to identical
 * under deuteranopia. So each mark differs from its neighbours in the family by
 * fill or silhouette as well as hue — see the stylesheet for the pairing.
 */
export type PitchKind = 'ball' | 'hbp' | 'called' | 'swinging' | 'foul' | 'inPlay';

export interface PitchMark {
  /** The stored character, kept for the hover text. */
  char: string;
  kind: PitchKind;
}

const KINDS: { [char: string]: PitchKind } = {
  B: 'ball',
  H: 'hbp',
  C: 'called',      // he watched it go by
  S: 'swinging',    // he missed it
  F: 'foul',
  X: 'inPlay',
};

/**
 * Measured on 818 real plate appearances: 94.6% hold six pitches or fewer,
 * 99.9% eight or fewer, and the longest seen was ten. Nothing in the rules caps
 * it, so the cell wraps rather than clips — but the common case is one row.
 */
export function pitchMarks(sequence: string | undefined | null): PitchMark[] {
  const marks: PitchMark[] = [];
  for (const char of String(sequence ?? '').toUpperCase()) {
    const kind = KINDS[char];
    if (kind) marks.push({char, kind});
  }
  return marks;
}

/** Hover text: how many pitches, and the sequence itself. */
export function pitchMarksTitle(marks: PitchMark[]): string {
  return `${marks.length} ${marks.length === 1 ? 'pitch' : 'pitches'}: ${marks.map(m => m.char).join('')}`;
}
